// Live's template cost (spec G14-template-costs): the tier bolts on a card and on the open form's
// pill, the pill's range chip, and the chip beside Generate. All from the template's `estimate`,
// live's declared cost of one output, none of them shown without one; the Generate chip from the
// run's own quote where its form quotes it (Actor Swap, Language Changer, Your Personal Clone,
// Talking Head Overlay, as live's).
import { Tooltip, VisuallyHidden } from "@mantine/core";
import { Zap } from "lucide-react";

import type { JobQuote, TemplateEstimate } from "../../api/generated/model";
import { tierWord } from "../../ui/cost-tier";
import classes from "./templates.module.css";

const amount = (credits: number) => credits.toLocaleString("en-US");

/** Live's note on the Generate chip where Generate Audio bills on its own
 *  (`AUDIO_BILLED_SEPARATELY_NOTE`, ui-templates `constants/generation.tsx:100`). */
export const AUDIO_NOTE = "Generating the audio is billed separately and is not part of this figure.";

/** Live's tooltip on a template's bolts (`TIER_HINT_SUFFIX`). */
export function tierHint(tier: number): string {
  return `${tierWord(tier)} token usage for one output of this template.`;
}

/** Live's pill sentence (`buildCostHint`): a range "around", a fixed cost as one figure. */
export function rangeHint({ credits_min: min, credits_max: max }: TemplateEstimate): string {
  const figure = min === max ? `${amount(min)} tokens` : `around ${amount(min)}–${amount(max)} tokens`;
  return `One output of this template costs ${figure}. Your own settings decide the final price.`;
}

/** Live's sentence on the Generate chip (`buildGenerationCostHint`), with a form's own note. */
export function generateHint(min: number, max: number, outputs: number, note?: string): string {
  const figure = min === max ? `${amount(min)} tokens` : `between ${amount(min)} and ${amount(max)} tokens`;
  const counted = outputs === 1 ? "1 output" : `${outputs} outputs`;
  const sentence = `Estimated cost of this Generate: ${figure} for ${counted}. The final cost may differ.`;
  return note === undefined ? sentence : `${sentence} ${note}`;
}

/**
 * Live's tier bolts (`PriceTierBadge`): one filled bolt a tier, its word in the tooltip and for a
 * screen reader. It takes no focus, as live's on a card, which is one button.
 */
export function TierBolts({ estimate }: { estimate: TemplateEstimate }) {
  const hint = tierHint(estimate.tier);
  return (
    <Tooltip label={hint} multiline maw={280} withArrow>
      <span className={classes.bolts}>
        {Array.from({ length: estimate.tier }, (_, at) => (
          <Zap key={at} size={12} fill="currentColor" aria-hidden />
        ))}
        <VisuallyHidden>{hint}</VisuallyHidden>
      </span>
    </Tooltip>
  );
}

interface ChipProps {
  min: number;
  max: number;
  hint: string;
  /** Live's approximate quote (a talking head's): filled as a range is. */
  approximate?: boolean;
}

/**
 * Live's chip in its two forms (`UftEstimate`): the bolt alone for a range, "⚡ n" for one figure;
 * the sentence in its tooltip and for a screen reader. A figure the number can't be trusted for on
 * its own, a range or an approximate quote, takes live's fill and the primary colour (`qualified`).
 */
function Chip({ min, max, hint, approximate = false }: ChipProps) {
  const qualified = approximate || min !== max;
  return (
    <Tooltip label={hint} multiline maw={360} withArrow>
      {/* An <output>, as the Studio's price chip: a status region, its sentence read out. */}
      <output className={qualified ? `${classes.costChip} ${classes.qualified}` : classes.costChip} aria-label={hint}>
        <Zap size={18} aria-hidden />
        {min === max ? <span aria-hidden>{amount(min)}</span> : null}
      </output>
    </Tooltip>
  );
}

/** The open form's pill (live's detailed `TemplateCost`): the bolts and the range chip. */
export function PillCost({ estimate }: { estimate: TemplateEstimate }) {
  return (
    <span className={classes.pillCost}>
      <TierBolts estimate={estimate} />
      <Chip min={estimate.credits_min} max={estimate.credits_max} hint={rangeHint(estimate)} />
    </span>
  );
}

export interface CostChipProps {
  estimate: TemplateEstimate | null | undefined;
  /** How many outputs this Generate makes (one a language, actor, hook, inspiration...). */
  outputs: number;
  /** Live's note where another control bills on its own (Generate Audio). */
  note?: string;
  /** The run's own quote, on the four forms live quotes (`usePricedStart`'s `lastPriced`). A 0 is
   *  none: the recipe quotes no run like this one (Actor Swap with lip sync on), so live's stored
   *  figure shows. */
  quote?: JobQuote | undefined;
}

/**
 * Live's chip beside Generate: the run's own quote where there is one, never multiplied, the
 * outputs in its sentence alone (`toRunQuoteBounds`); else the template's cost of one output times
 * the outputs this click makes (`useGenerationCostEstimate`). None without either, and none while
 * the form makes no output yet: a zero or a guess would read as a quote. The staged forms show none
 * (live's).
 */
export function CostChip({ estimate, outputs, note, quote }: CostChipProps) {
  if (quote !== undefined && quote.credits_max > 0) {
    const { credits_min: min, credits_max: max, approximate } = quote;
    return <Chip min={min} max={max} approximate={approximate} hint={generateHint(min, max, outputs, note)} />;
  }
  if (estimate === null || estimate === undefined || !Number.isFinite(outputs) || outputs < 1) return null;
  const min = estimate.credits_min * outputs;
  const max = estimate.credits_max * outputs;
  return <Chip min={min} max={max} hint={generateHint(min, max, outputs, note)} />;
}
